import { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useRefetchableFragment } from "react-relay";

import { useFeature, useSwitch } from "~/lib/flippy";
import { isZoom } from "~/lib/utils";
import WebsocketClient from "~/lib/websocket";
import type { SelectorOption } from "~/ui/selectors/Selector";

import type { useConferencingOptions_viewer$key } from "~/__generated__/useConferencingOptions_viewer.graphql";
import type { ConferenceProviderOptions as ConferenceProvider } from "~/__generated__/useConferencingOptions_viewer.graphql";

export type { ConferenceProvider };

export interface ConferenceOption extends SelectorOption {
	value: ConferenceProvider;
	needsSetup?: boolean;
}

type ConferenceProviderName = {
	[key in ConferenceProvider]: string;
};

export const CONFERENCE_PROVIDERS: ConferenceProviderName = {
	GOOGLE: "Google Meet",
	ZOOM: "Zoom",
	TEAMS: "Microsoft Teams",
	NONE: "None",
} as const;

export const viewerFragment = graphql`
	fragment useConferencingOptions_viewer on Viewer @refetchable(queryName: "useConferencingOptionsRefreshQuery") {
		id
		defaultConferenceProvider
		user {
			id
		}
		integrations {
			zoom {
				id
				hasMeetingWriteScope
			}
		}
		calendars {
			edges {
				node {
					id
					source
				}
			}
		}
	}
`;

export function getProviderFromPlatform(platform?: string): ConferenceProvider {
	if (platform == "HANGOUTS_MEET") {
		return "GOOGLE";
	} else if (platform == "ZOOM") {
		return "ZOOM";
	} else if (platform == "MS_TEAMS") {
		return "TEAMS";
	}

	return "NONE";
}

export default function useConferencingOptions(
	viewerKey: useConferencingOptions_viewer$key,
	initialProvider?: ConferenceProvider,
) {
	const [viewer, refetchZoomQuery] = useRefetchableFragment(viewerFragment, viewerKey ?? null);

	const [hasMeetingWriteScope, setHasMeetingWriteScope] = useState(viewer?.integrations.zoom?.hasMeetingWriteScope);

	const zoomConferenceFeatureEnabled = useFeature("zoom_conference_provider", "account") ?? false;
	const zoomConferenceSwitchEnabled = useSwitch("zoom_conference_provider") ?? false;

	const hasGoogleCalendar = viewer?.calendars?.edges.find(c => c?.node?.source == "google");
	const hasOfficeCalendar = viewer?.calendars?.edges.find(c => c?.node?.source == "office365");

	const conferenceOptions = useMemo(() => {
		let options: ConferenceOption[] = [];

		if (hasGoogleCalendar) {
			options.push({
				value: "GOOGLE",
				label: CONFERENCE_PROVIDERS["GOOGLE"],
			});
		}

		if (hasOfficeCalendar) {
			options.push({
				value: "TEAMS",
				label: CONFERENCE_PROVIDERS["TEAMS"],
			});
		}

		options.push(
			{
				value: "ZOOM",
				label: CONFERENCE_PROVIDERS["ZOOM"],
				needsSetup: !hasMeetingWriteScope,
			},
			{
				value: "NONE",
				label: CONFERENCE_PROVIDERS["NONE"],
			},
		);

		return options;
	}, [hasMeetingWriteScope, hasGoogleCalendar, hasOfficeCalendar]);

	const [conferenceProvider, setConferenceProvider] = useState<ConferenceProvider>(() => {
		if (initialProvider) return initialProvider;
		if (isZoom && (zoomConferenceFeatureEnabled || zoomConferenceSwitchEnabled)) return "ZOOM";
		if (viewer?.defaultConferenceProvider) return viewer.defaultConferenceProvider;
		if (hasGoogleCalendar) return "GOOGLE";
		if (hasOfficeCalendar) return "TEAMS";
		return "NONE";
	});

	const changeConferenceProvider = useCallback(
		(newProvider: ConferenceProvider) => {
			if (newProvider === "ZOOM" && !hasMeetingWriteScope) {
				window.open("/zoom/oauth/", "_blank", "noopener noreferrer");
			} else {
				setConferenceProvider(newProvider);
			}
		},
		[hasMeetingWriteScope],
	);

	useEffect(() => {
		const userId = viewer?.user.id;
		const socket = WebsocketClient.getInstance().getOrJoinGroup(`integrations.${userId}`.replace(/=/g, ""), { userId });

		async function onSocketMessage(message: { type: string }) {
			if (message.type == "ZOOM_CONNECTED") {
				setHasMeetingWriteScope(true);
				setConferenceProvider("ZOOM");
				refetchZoomQuery({}, { fetchPolicy: "network-only" });
			}
		}

		socket.on("message", onSocketMessage);

		return () => {
			socket.off("message", onSocketMessage);
			socket.close();
		};
	}, [setConferenceProvider, refetchZoomQuery, viewer?.user.id]);

	return { conferenceProvider, setConferenceProvider: changeConferenceProvider, conferenceOptions };
}
